import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteClient, getClients, importerClientsCsv } from '../services/clientService';
import ClientFormulaire from './ClientFormulaire';
import { STATUT_LABELS, formatDate, formaterTelephone } from './devisUtils';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Clients.css';
import { FlashToast } from '../components/Toasts';
import BarreProgression, { useProgression } from '../components/BarreProgression';

const LIMIT = 20;

const COLONNES = [
  { cle: 'nom', label: 'CONTACT' },
  { cle: null, label: 'ENTREPRISE' },
  { cle: null, label: 'STATUT' },
  { cle: null, label: 'TÉLÉPHONES' },
  { cle: 'derniere', label: 'DEMANDES' },
  { cle: null, label: 'PIÈCES' },
];

const LIBELLE_SOURCE = { brevo: 'Brevo', demande: 'Demande', manuel: 'Saisi' };

/**
 * Teinte de l'avatar, tirée de l'email.
 *
 * Déterministe : le même contact garde sa couleur d'une page à l'autre, ce
 * qui aide à le repérer dans une liste de près de deux mille lignes. Les
 * teintes sont espacées sur le cercle chromatique, à saturation et clarté
 * constantes, pour rester lisibles et ne pas jurer entre elles.
 */
const TEINTES = [8, 32, 96, 150, 190, 215, 260, 290, 330];

function couleurAvatar(email) {
  const texte = String(email || '');
  let somme = 0;
  for (let i = 0; i < texte.length; i += 1) somme = (somme + texte.charCodeAt(i) * (i + 1)) % 9973;
  return TEINTES[somme % TEINTES.length];
}

/** Deux initiales : prénom + nom, sinon les deux premières lettres de l'email. */
function initiales(client) {
  const prenom = (client.prenom || '').trim();
  const nom = (client.nom || '').trim();
  if (prenom || nom) return ((prenom[0] || '') + (nom[0] || '')).toUpperCase();
  return String(client.email || '?')
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, 2)
    .toUpperCase();
}

/** Pastille ronde à initiales, comme dans les carnets de contacts. */
function Avatar({ client }) {
  const teinte = couleurAvatar(client.email);
  return (
    <span
      className="clients-avatar"
      style={{
        background: `hsl(${teinte} 42% 92%)`,
        color: `hsl(${teinte} 52% 30%)`,
      }}
      aria-hidden="true"
    >
      {initiales(client)}
    </span>
  );
}

/**
 * État d'abonnement, tel qu'il vient de Brevo. Sans valeur, on n'affiche
 * rien : ne pas savoir n'est pas la même chose qu'être désabonné.
 */
function PastillesStatut({ client }) {
  return (
    <div className="clients-etats">
      {client.bloque === true && <span className="clients-etat clients-etat-bloque">Bloqué</span>}
      {client.abonne === true && <span className="clients-etat clients-etat-abonne">Abonné</span>}
      {client.abonne === false && (
        <span className="clients-etat clients-etat-desabonne">Désabonné</span>
      )}
      <span className="clients-source">{LIBELLE_SOURCE[client.source] || client.source}</span>
    </div>
  );
}

/**
 * Colonnes de l'export Brevo que l'import sait lire (cf. ENTETES_BREVO côté
 * serveur). Aucune n'est obligatoire ; une colonne cochée en plus ne bloque
 * rien, elle est juste signalée « non reconnue » dans le bilan.
 */
const COLONNES_BREVO = [
  ['EMAIL', 'recommandée : relie la fiche à ses demandes et évite les doublons'],
  ['CONTACT ID', 'recommandée : reconnaît une fiche sans email d’un import à l’autre'],
  ['NOM', ''],
  ['PRENOM', ''],
  ['SEXE', ''],
  ['ABONNE', ''],
  ['BLOQUE', 'ou BLACKLISTED'],
  ['UTILISATEUR ASSIGNE', ''],
  ['TELEPHONE', 'téléphone fixe'],
  ['WHATSAPP', 'ou SMS'],
  ['DERNIERE MODIFICATION', ''],
  ['DATE DE CREATION', ''],
  ['CONTACT', 'l’attribut personnalisé'],
  ['SECTEUR D’ACTIVITE', ''],
  ['FONCTION', ''],
];

/**
 * Petit guide affiché sous l'en-tête quand on veut importer : comment
 * exporter depuis Brevo, quelles colonnes cocher, puis le choix du fichier.
 */
function GuideImportBrevo({ enCours, onChoisir, onFermer }) {
  const progression = useProgression(enCours, { estimeeMs: 12000 });
  return (
    <section className="clients-guide" aria-label="Guide d’import Brevo">
      <div className="clients-guide-tete">
        <h2 className="clients-guide-titre">Importer les contacts depuis Brevo</h2>
        <button type="button" className="clients-guide-fermer" onClick={onFermer} aria-label="Fermer le guide">
          ×
        </button>
      </div>

      <div className="clients-guide-colonnes">
        <div>
          <h3 className="clients-guide-sous-titre">1. Dans Brevo</h3>
          <ol className="clients-guide-etapes">
            <li>
              Ouvrez <strong>Contacts</strong>, puis la liste à exporter (ou tous les contacts).
            </li>
            <li>
              Cliquez sur <strong>Exporter</strong> et choisissez le format <strong>CSV</strong>.
            </li>
            <li>Cochez les colonnes ci-contre, puis lancez l’export.</li>
            <li>Récupérez le fichier depuis l’e-mail Brevo ou la page des exports.</li>
          </ol>
        </div>

        <div>
          <h3 className="clients-guide-sous-titre">2. Colonnes à cocher (toutes facultatives)</h3>
          <ul className="clients-guide-liste">
            {COLONNES_BREVO.map(([nom, note]) => (
              <li key={nom}>
                <code>{nom}</code>
                {note && <span className="clients-guide-note"> — {note}</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="clients-guide-info">
        Un client existant (même email) est complété ; une case vide n’efface rien.
      </p>

      <BarreProgression valeur={progression} libelle="Import du fichier CSV" estimation />
      <div className="clients-guide-actions">
        <button type="button" className="devis-btn" onClick={onFermer} disabled={enCours}>
          Annuler
        </button>
        <button
          type="button"
          className="devis-btn devis-btn-primary"
          onClick={onChoisir}
          disabled={enCours}
        >
          {enCours ? 'Import en cours' : '3. Choisir le fichier CSV'}
        </button>
      </div>
    </section>
  );
}

/**
 * Clients > Liste des clients : le fichier importé de Brevo, complété par
 * les demandes de devis. Un clic ouvre le dossier du client (ses demandes,
 * devis, contrats, pièces). Ce que les clients ont fait (signatures,
 * refus, dépôts) se lit dans la cloche de la barre latérale, pas ici.
 */
function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  const [sortBy, setSortBy] = useState('nom');
  const [sortDir, setSortDir] = useState('ASC');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);

  const [creation, setCreation] = useState(false);
  const [flash, setFlash] = useState(null);

  // Import CSV : un input file caché derrière le bouton.
  const inputImport = useRef(null);
  const [importEnCours, setImportEnCours] = useState(false);
  const [bilanImport, setBilanImport] = useState(null);
  // Le guide d'export Brevo : ouvert par le bouton d'import, il porte le
  // choix du fichier — on lit les colonnes à cocher avant de choisir le CSV.
  const [guideOuvert, setGuideOuvert] = useState(false);

  // Suppression d'une fiche : celle qu'on s'apprête à effacer (la
  // confirmation est ouverte) et l'appel en cours.
  const [suppression, setSuppression] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);

  const nomClient = (c) =>
    [c.prenom, c.nom].filter(Boolean).join(' ') || c.email || c.societeNom || `Client ${c.id}`;

  async function handleSupprimer() {
    if (!suppression) return;
    const cible = suppression;
    setSuppressionEnCours(true);
    try {
      const res = await deleteClient(cible.id);
      const n = res.data?.demandesDetachees || 0;
      setFlash(
        `Fiche de ${nomClient(cible)} supprimée${
          n ? ` (${n} demande${n > 1 ? 's' : ''} conservée${n > 1 ? 's' : ''}, détachée${n > 1 ? 's' : ''})` : ''
        }.`
      );
      setSuppression(null);
      setReload((k) => k + 1);
    } catch (err) {
      setFlash(`Suppression impossible : ${err.message || 'erreur inconnue'}.`);
    } finally {
      setSuppressionEnCours(false);
    }
  }


  useEffect(() => {
    const timer = setTimeout(() => {
      setRecherche(saisie);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [saisie]);

  useEffect(() => {
    let annule = false;
    setLoading(true);
    setError(null);
    getClients({ page, limit: LIMIT, search: recherche, sortBy, sortDir })
      .then((res) => {
        if (annule) return;
        setClients(res.data || []);
        setTotal(res.pagination?.total ?? 0);
      })
      .catch(() => {
        if (!annule) setError('Impossible de charger les clients.');
      })
      .finally(() => {
        if (!annule) setLoading(false);
      });
    return () => {
      annule = true;
    };
  }, [page, recherche, sortBy, sortDir, reload]);

  function trier(cle) {
    if (!cle) return;
    if (cle === sortBy) setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    else {
      setSortBy(cle);
      setSortDir('ASC');
    }
    setPage(1);
  }

  async function handleImport(e) {
    const fichier = e.target.files?.[0];
    e.target.value = '';
    if (!fichier) return;
    setImportEnCours(true);
    setBilanImport(null);
    setError(null);
    try {
      const res = await importerClientsCsv(fichier);
      setBilanImport(res.data);
      setGuideOuvert(false);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err.message || 'Import impossible.');
    } finally {
      setImportEnCours(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="devis-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Clients</h1>
          <p className="devis-subtitle">
            Le fichier clients, et pour chacun ses demandes, devis, contrats et pièces.
          </p>
        </div>
        <div className="devis-header-actions">
          <input
            ref={inputImport}
            type="file"
            accept=".csv,text/csv"
            className="clients-import-input"
            onChange={handleImport}
          />
          <button
            type="button"
            className={'devis-btn' + (guideOuvert ? ' clients-btn-actif' : '')}
            onClick={() => setGuideOuvert((o) => !o)}
            disabled={importEnCours}
            aria-expanded={guideOuvert}
            title="Export CSV de Brevo : les clients existants (même email) sont mis à jour"
          >
            {importEnCours ? 'Import en cours' : 'Importer un CSV Brevo'}
          </button>
          <button type="button" className="devis-btn devis-btn-primary" onClick={() => setCreation(true)}>
            + Nouveau client
          </button>
        </div>
      </div>

      <div className="devis-toolbar">
        <input
          type="text"
          className="devis-search"
          placeholder="Rechercher un nom, un email, une société..."
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
        />
        <span className="devis-total">{total} client{total > 1 ? 's' : ''}</span>
      </div>

      <FlashToast texte={flash} onFermer={() => setFlash(null)} />

      {guideOuvert && (
        <GuideImportBrevo
          enCours={importEnCours}
          onChoisir={() => inputImport.current?.click()}
          onFermer={() => setGuideOuvert(false)}
        />
      )}

      {bilanImport && (
        <div className="devis-state">
          Import terminé : {bilanImport.crees} créé{bilanImport.crees > 1 ? 's' : ''},{' '}
          {bilanImport.misAJour} mis à jour, {bilanImport.ignores} ignoré
          {bilanImport.ignores > 1 ? 's' : ''} sur {bilanImport.total} ligne
          {bilanImport.total > 1 ? 's' : ''}.
          {bilanImport.sansEmail > 0 && (
            <>
              {' '}
              {bilanImport.sansEmail} fiche{bilanImport.sansEmail > 1 ? 's' : ''} sans email.
            </>
          )}
          {bilanImport.colonnesIgnorees?.length > 0 && (
            <> Colonnes non reconnues : {bilanImport.colonnesIgnorees.join(', ')}.</>
          )}
          {bilanImport.erreurs?.length > 0 && (
            <ul>
              {bilanImport.erreurs.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="devis-card">
        <div className="devis-table-wrap">
          {loading && <div className="devis-state">Chargement des clients...</div>}
          {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

          {!loading && !error && clients.length === 0 && (
            <div className="devis-empty">
              <div className="devis-empty-title">Aucun client ne correspond.</div>
              <div className="devis-empty-subtitle">
                Importez un CSV Brevo ou créez un client.
              </div>
            </div>
          )}

          {!loading && !error && clients.length > 0 && (
            <div className="clients-cartes">
              {/* Le tri, qui vivait dans les en-têtes du tableau. */}
              <div className="clients-cartes-tri">
                <span>Trier par</span>
                {COLONNES.filter((col) => col.cle).map((col) => (
                  <button
                    key={col.cle}
                    type="button"
                    className={'clients-tri' + (sortBy === col.cle ? ' clients-tri-actif' : '')}
                    onClick={() => trier(col.cle)}
                  >
                    {col.label.charAt(0) + col.label.slice(1).toLowerCase()}
                    <span className="devis-sort-icon devis-sort-icon-active">
                      {sortBy === col.cle ? (sortDir === 'ASC' ? '↑' : '↓') : '↕'}
                    </span>
                  </button>
                ))}
              </div>

              {clients.map((c) => (
                <article
                  key={c.id}
                  className="clients-carte"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/clients/${c.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/clients/${c.id}`);
                    }
                  }}
                >
                  <div className="clients-carte-tete">
                    {/* Sans nom (fréquent dans l'export Brevo), l'email tient
                        lieu de libellé plutôt qu'un tiret ; sans l'un ni
                        l'autre (066), le téléphone ou l'identifiant Brevo. */}
                    <div className="clients-identite">
                      <Avatar client={c} />
                      <div className="clients-identite-texte">
                        <div className="clients-carte-nom">
                          {[c.prenom, c.nom].filter(Boolean).join(' ') ||
                            c.email ||
                            c.telephoneFixe ||
                            c.whatsapp ||
                            (c.identifiantBrevo ? `Contact Brevo ${c.identifiantBrevo}` : 'Sans nom')}
                        </div>
                        {(c.prenom || c.nom) && c.email && (
                          <div className="clients-email">{c.email}</div>
                        )}
                      </div>
                    </div>
                    <PastillesStatut client={c} />
                  </div>

                  <div className="clients-carte-entreprise">
                    {/* Le tiret seulement quand on ne sait rien : ni société,
                        ni fonction, ni secteur. */}
                    {(c.societeNom || !(c.fonction || c.secteurActivite)) && (
                      <span className="devis-contact-name">{c.societeNom || '—'}</span>
                    )}
                    {[c.fonction, c.secteurActivite].filter(Boolean).map((a) => (
                      <span className="clients-attribut" key={a}>
                        {a}
                      </span>
                    ))}
                  </div>

                  <div className="clients-carte-pied">
                    <div className="clients-carte-cellule">
                      <span className="clients-carte-libelle">TÉLÉPHONES</span>
                      <div className="clients-tel">{formaterTelephone(c.telephoneFixe) || '—'}</div>
                      {c.whatsapp && <div className="clients-email">WhatsApp {formaterTelephone(c.whatsapp)}</div>}
                    </div>
                    <div className="clients-carte-cellule">
                      <span className="clients-carte-libelle">DEMANDES</span>
                      {c.nbDemandes > 0 ? (
                        <>
                          <span className="devis-status-pill">
                            {c.nbDemandes} demande{c.nbDemandes > 1 ? 's' : ''}
                          </span>
                          <div className="devis-status-detail">
                            {STATUT_LABELS[c.dernierStatut] || c.dernierStatut || ''}
                            {c.derniereDemande ? ` · ${formatDate(c.derniereDemande)}` : ''}
                          </div>
                        </>
                      ) : (
                        <span className="devis-status-detail">aucune</span>
                      )}
                    </div>
                    <div className="clients-carte-cellule">
                      <span className="clients-carte-libelle">PIÈCES</span>
                      <div>{c.nbDocuments > 0 ? c.nbDocuments : '—'}</div>
                    </div>
                    <div
                      className="clients-carte-cellule clients-carte-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="clients-carte-libelle">ACTIONS</span>
                      <div className="clients-carte-boutons">
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm devis-btn-primary"
                          onClick={() => navigate(`/clients/${c.id}`)}
                        >
                          Dossier
                        </button>
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm devis-btn-danger"
                          onClick={() => setSuppression(c)}
                          disabled={suppressionEnCours}
                          title="Supprimer cette fiche client"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {!loading && !error && total > 0 && (
          <div className="devis-pagination">
            <span className="devis-pagination-info">
              {(page - 1) * LIMIT + 1}-{Math.min(page * LIMIT, total)} sur {total}
            </span>
            <div className="devis-pagination-controls">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Précédent
              </button>
              <span className="devis-pagination-page">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {suppression && (
        <div
          className="devis-modal-overlay"
          onClick={suppressionEnCours ? undefined : () => setSuppression(null)}
        >
          <div
            className="devis-modal devis-modal-etroit"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-labelledby="suppression-client-titre"
          >
            <div className="devis-modal-header">
              <div>
                <div className="devis-modal-numero" id="suppression-client-titre">
                  Supprimer cette fiche client ?
                </div>
                <div className="devis-modal-sub">{nomClient(suppression)}</div>
              </div>
              <div className="devis-modal-header-right">
                <button
                  type="button"
                  className="devis-drawer-close"
                  onClick={() => setSuppression(null)}
                  disabled={suppressionEnCours}
                  aria-label="Fermer"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="devis-modal-body">
              <div className="devis-alert devis-alert-erreur">
                <strong>Mise à la corbeille.</strong> La fiche quitte le fichier Clients ; elle reste
                restaurable pendant 30 jours.
                {suppression.nbDemandes > 0 ? (
                  <div style={{ marginTop: 6 }}>
                    Ses {suppression.nbDemandes} demande{suppression.nbDemandes > 1 ? 's' : ''} de
                    devis sont conservées, sans fiche client.
                  </div>
                ) : (
                  <div style={{ marginTop: 6 }}>Aucune demande de devis n&apos;y est rattachée.</div>
                )}
              </div>
              <div className="devis-modal-actions">
                <button
                  type="button"
                  className="devis-btn"
                  onClick={() => setSuppression(null)}
                  disabled={suppressionEnCours}
                >
                  Non, garder
                </button>
                <button
                  type="button"
                  className="devis-btn devis-btn-danger"
                  onClick={handleSupprimer}
                  disabled={suppressionEnCours}
                  autoFocus
                >
                  {suppressionEnCours ? 'Suppression…' : 'Oui, supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {creation && (
        <ClientFormulaire
          onClose={() => setCreation(false)}
          onEnregistre={(client) => {
            setCreation(false);
            setFlash(`Client ${[client.prenom, client.nom].filter(Boolean).join(' ')} créé.`);
            setReload((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}

export default Clients;
